import { TextField } from '@cxc/react-fds-components'
import {
  ChangeEvent,
  KeyboardEventHandler,
  useEffect,
  useRef,
  useState,
} from 'react'
import { CustomerInfoFormInput } from './customerInfoValidation'
import { ControllerRenderProps, FieldErrors } from 'react-hook-form'
import { useFetchAddress } from '../../hooks/useFetchAddress'
import { AutoCompleteAddress } from '../../types/hereMaps'
import { UseFormSetValue } from 'react-hook-form/dist/types/form'

interface AddressFieldWithSuggestionsProps {
  field: ControllerRenderProps<CustomerInfoFormInput, 'streetAddress1'>
  errors: FieldErrors<CustomerInfoFormInput>
  setValue: UseFormSetValue<CustomerInfoFormInput>
  handleFetchAddressError: (errorMessage: string) => void
  placeholder: string
}

function moveIndexDown(prevIndex: number | null, arrayLength: number) {
  const indexIsNull = prevIndex === null
  const indexIsAtEnd = prevIndex === arrayLength - 1
  return indexIsNull || indexIsAtEnd ? 0 : prevIndex + 1
}

function moveIndexUp(prevIndex: number | null, arrayLength: number) {
  const indexIsNull = prevIndex === null
  const indexIsAtStart = prevIndex === 0
  return indexIsNull || indexIsAtStart ? arrayLength - 1 : prevIndex - 1
}

export function AddressFieldWithSuggestions({
  field,
  errors,
  setValue,
  handleFetchAddressError,
  placeholder,
}: AddressFieldWithSuggestionsProps) {
  const {
    suggestedAddresses,
    fetchAddress,
    showAddressSuggest,
    setShowAddressSuggest,
  } = useFetchAddress(handleFetchAddressError)

  const [focusedIndex, setFocusedIndex] = useState<number | null>(null)
  const listRef = useRef<HTMLUListElement>(null)

  useEffect(() => {
    if (focusedIndex !== null && listRef.current) {
      const options: NodeListOf<HTMLElement> =
        listRef.current.querySelectorAll('[role="option"]')

      options[focusedIndex]?.focus()
    }
  }, [focusedIndex])

  const updateFieldWithAddress = ({ address }: AutoCompleteAddress) => {
    const streetAddress1 =
      (address.houseNumber ?? address.district ?? '') +
      ' ' +
      (address.street ?? '')
    setValue('streetAddress1', streetAddress1, {
      shouldValidate: true,
      shouldDirty: true,
    })
    setValue('city', address.city ?? '', {
      shouldValidate: true,
      shouldDirty: true,
    })
    setValue(
      'stateProvince',
      {
        label: address.stateCode ?? '',
        value: address.state.replace(/\s+/g, '') ?? '',
      },
      {
        shouldValidate: true,
        shouldDirty: true,
      },
    )
    setValue('postalCode', address?.postalCode ?? '', {
      shouldValidate: true,
      shouldDirty: true,
    })
    setShowAddressSuggest(false)
    setFocusedIndex(null)
  }

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (showAddressSuggest && suggestedAddresses.length > 0) {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault()
          setFocusedIndex((prevIndex) =>
            moveIndexDown(prevIndex, suggestedAddresses.length),
          )
          break
        case 'ArrowUp':
          event.preventDefault()
          setFocusedIndex((prevIndex) =>
            moveIndexUp(prevIndex, suggestedAddresses.length),
          )
          break
        case 'Enter':
          event.preventDefault()
          if (focusedIndex !== null) {
            updateFieldWithAddress(suggestedAddresses[focusedIndex])
          }
          break
        case 'Escape':
          event.preventDefault()
          setShowAddressSuggest(false)
          setFocusedIndex(null)
          break
        default:
          break
      }
    }
  }

  return (
    <>
      <TextField
        {...field}
        message={errors[field.name]?.message}
        error={errors[field.name]}
        placeholder={placeholder}
        onChange={async (event: ChangeEvent<HTMLInputElement>) => {
          setValue('streetAddress1', event.target.value)
          await fetchAddress(event.target.value)
        }}
        onBlur={() => {
          setShowAddressSuggest(false)
          field.onBlur()
        }}
        onKeyDown={handleKeyDown}
        role={'combobox'}
        aria-haspopup={'listbox'}
        aria-controls={'address-listbox'}
        aria-expanded={showAddressSuggest}
      />
      <ul
        ref={listRef}
        className={`list-styles border-style ${
          showAddressSuggest ? 'show' : 'hide-address-suggest'
        }`}
        role={'listbox'}
        id={'address-listbox'}
        tabIndex={-1}
        aria-multiselectable={false}
      >
        {suggestedAddresses.map((data, index) => {
          const isFocusedItem = focusedIndex === index
          return (
            <li
              className={`fds-dropdown__list-option ${
                isFocusedItem ? 'fds-dropdown__list-option--focused' : ''
              }`}
              role={'option'}
              key={data.id}
              onClick={() => updateFieldWithAddress(data)}
              id={`address-option-${data.id}`}
              aria-selected={isFocusedItem}
            >
              {data.address?.label}
            </li>
          )
        })}
      </ul>
    </>
  )
}
